/**
 * Connections — every external dependency the firm has, grouped, with a light.
 *
 * Alan, 2026-08-31: *"make sure that there is a page on the config that manages
 * the connections to everything and has red or green indicators with the
 * ability to paste in a new key from the interface. break it down by types of
 * connections like news, accounts, or llm providers."*
 *
 * The old page listed seven vault kinds and knew nothing about Kraken, OANDA,
 * FRED, Finnhub, EIA, fock.space or mail — the credentials that actually run
 * the firm. Worse, its list and the nightly audit's list disagreed, which is
 * how `google` and `mistral` sat keyless for a month with the audit reporting
 * all-clear. Both now read one registry on the server.
 *
 * Money first, then the models, then data. The ordering is editorial: a red
 * dot on REAL crypto execution and a red dot on the earnings feed are not the
 * same emergency and the page should not pretend otherwise.
 */
import { useEffect, useState } from "react";
import { api, ConnField, ConnStatus, ConnectionsStatus, Manifest } from "../api";

const LIGHT: Record<string, string> = {
  green: "bg-up",
  amber: "bg-gold",
  red: "bg-down",
};

/** What the state word means in the owner's terms, not the vendor's. */
const STATE_LABEL: Record<string, string> = {
  ok: "working",
  stale: "rejected",
  expired: "expired",
  expiring: "expires soon",
  unverified: "couldn't check",
  unconfigured: "not set up",
};

const SOURCE_LABEL: Record<string, string> = {
  vault: "key set here",
  env: "server default",
  missing: "no key",
  keyless: "no key needed",
};

export default function Connections(_: { manifest: Manifest }) {
  const [data, setData] = useState<ConnectionsStatus | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<Record<string, string>>({});

  useEffect(() => {
    void load(false);
  }, []);

  async function load(refresh: boolean) {
    setBusy(refresh ? "*" : busy);
    setError(null);
    try {
      setData(await api.get<ConnectionsStatus>(`/api/connections/status?refresh=${refresh}`));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function save(c: ConnStatus) {
    setBusy(c.key);
    setError(null);
    try {
      const payload: Record<string, unknown> = {};
      for (const f of c.fields) {
        const v = form[f.key]?.trim();
        if (v) payload[f.key] = v;
      }
      if (!Object.keys(payload).length) {
        setError("Nothing to save — paste a key first.");
        return;
      }
      await api.put("/api/connections", { kind: c.vault_kind, payload });
      setEditing(null);
      setForm({});
      await probe(c, true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function probe(c: ConnStatus, quiet = false) {
    if (!quiet) setBusy(c.key);
    try {
      const r = await api.post<{ light: string; detail: string }>(
        `/api/connections/probe/${c.key}`,
      );
      setNote({ ...note, [c.key]: `${r.light}: ${r.detail}` });
      await load(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      if (!quiet) setBusy(null);
    }
  }

  if (!data) {
    return (
      <div className="space-y-4">
        <h1 className="text-lg font-bold">Connections</h1>
        {error ? <div className="text-down text-sm">{error}</div> : <div className="text-sm text-slate-400">Checking every connection…</div>}
      </div>
    );
  }

  const { red, amber, green } = data.summary;

  return (
    <div className="space-y-5">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h1 className="text-lg font-bold">Connections</h1>
        <button className="btn" onClick={() => void load(true)} disabled={busy === "*"}>
          {busy === "*" ? "Testing…" : "Re-test everything"}
        </button>
      </div>

      <div className="flex items-center gap-4 text-sm">
        <Dot light="green" /> <span>{green} working</span>
        <Dot light="amber" /> <span>{amber} needs a look</span>
        <Dot light="red" /> <span>{red} broken</span>
        <span className="text-xs text-slate-500">
          checked {new Date(data.checked_at).toLocaleString()}
        </span>
      </div>

      <p className="text-sm text-slate-400">
        Every key is <em>exercised</em>, not just checked for presence — the same probe the nightly
        report runs, so a green dot here and an all-clear in the email mean the same thing. Keys
        pasted here are encrypted at rest and override the server defaults; they are write-only,
        so only the last four characters ever come back.
      </p>

      {error && <div className="text-down text-sm">{error}</div>}

      {data.groups.map((g) => (
        <section key={g.key} className="space-y-2">
          <div>
            <h2 className="font-semibold text-sm">{g.title}</h2>
            <p className="text-xs text-slate-500">{g.blurb}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            {g.items.map((c) => (
              <div key={c.key} className="card space-y-2">
                <div className="flex items-center gap-2">
                  <Dot light={c.light} />
                  <span className="font-semibold text-sm">{c.title}</span>
                  {c.critical && (
                    <span className="text-[10px] uppercase tracking-wide text-slate-500">
                      critical
                    </span>
                  )}
                  <span className="ml-auto text-xs text-slate-400">
                    {STATE_LABEL[c.state] ?? c.state}
                  </span>
                </div>

                <p className="text-xs text-slate-400">{c.gates}</p>

                <div className="text-xs text-slate-500 space-y-0.5">
                  <div>
                    {SOURCE_LABEL[c.source]}
                    {c.fingerprint && <span className="ml-1 font-mono">{c.fingerprint}</span>}
                  </div>
                  {(note[c.key] ?? c.detail) && (
                    <div className="break-words">{note[c.key] ?? c.detail}</div>
                  )}
                  {c.state === "stale" && c.last_ok_days_ago != null && (
                    <div className="text-down">last worked {c.last_ok_days_ago}d ago</div>
                  )}
                  {c.expires_on && <div>expires {c.expires_on}</div>}
                </div>

                {editing === c.key ? (
                  <Editor
                    fields={c.fields}
                    form={form}
                    setForm={setForm}
                    busy={busy === c.key}
                    onSave={() => void save(c)}
                    onCancel={() => {
                      setEditing(null);
                      setForm({});
                    }}
                  />
                ) : (
                  <div className="flex gap-2">
                    {c.pasteable && (
                      <button
                        className="btn"
                        onClick={() => {
                          setEditing(c.key);
                          setForm({});
                        }}
                      >
                        {c.source === "missing" ? "Add key" : "Replace key"}
                      </button>
                    )}
                    <button className="btn" onClick={() => void probe(c)} disabled={busy === c.key}>
                      {busy === c.key ? "…" : "Test"}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function Dot({ light }: { light: string }) {
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${LIGHT[light] ?? "bg-slate-600"}`} />;
}

function Editor({
  fields,
  form,
  setForm,
  busy,
  onSave,
  onCancel,
}: {
  fields: ConnField[];
  form: Record<string, string>;
  setForm: (f: Record<string, string>) => void;
  busy: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="space-y-2">
      {fields.map((f) => (
        <div key={f.key}>
          <label className="label">{f.label}</label>
          <input
            className="input"
            type={f.secret ? "password" : "text"}
            autoComplete="off"
            placeholder={f.placeholder}
            value={form[f.key] ?? ""}
            onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
          />
        </div>
      ))}
      <p className="text-[11px] text-slate-500">
        Leave a field blank to keep what is already there. Saving tests the new key immediately.
      </p>
      <div className="flex gap-2">
        <button className="btn btn-primary" onClick={onSave} disabled={busy}>
          {busy ? "…" : "Save & test"}
        </button>
        <button className="btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
